import type {
  IViewColorConfig,
  IViewConfig,
  StylesConfig,
} from "lola-framework-ui-test";
import { config } from "../config";

export enum RequestStatus {
  NOT_LOADED,
  lOADING,
  lOADED,
  ERROR,
}

export const errorInitialState = {
  title: null,
  subTitle: null,
  message: null,
  btnText: null,
  isOut: false,
};

export const cardStateInitialState = {
  cardId: null,
  name: null,
  brand: null,
  last4: null,
  accountNumber: null,
  expirationDate: null,
  fullName: null,
  cvv: null,
};

/**
 * Fallback StylesConfig when theme fetch/claim fails or omits `styles`.
 * Mirrors kit DEFAULT_STYLES so layout stays intact without CSS hardcodes.
 */
export const defaultThemeStyles: StylesConfig = {
  cardBorderRadius: "20px",
  buttonBorderRadius: "20px",
  inputBorderRadius: "10px",
  cardBorderColor: "#E4E4E4",
  inputBorderColor: "#E4E4E4",
  activeBorderBoton: "#1DAFA1",
  tamañoBordeCard: "1px",
  tamañoBordeInput: "1px",
  buttonSize: "small",
  buttonShowIcon: false,
  iconContainerBackground: "transparent",
  buttonPadding: "0.75rem",
  inputPadding: "0.75rem",
  cardPadding: "1.5rem",
};

export const themeInitialState = {
  font: {
    h1: {
      fontWeight: "400",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "1",
    },
    highlight: {
      fontWeight: "700",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "0.95",
    },
    h2: {
      fontWeight: "600",
      min: "1.25rem",
      max: "1.5rem",
      lineHeight: "1",
    },
    bodycopy: {
      fontWeight: "500",
      min: "1rem",
      max: "1.25rem",
      lineHeight: "1.25rem",
    },
    secondaryCta: {
      fontWeight: "500",
      min: "0.74rem",
      max: "1rem",
      lineHeight: "1",
    },
    footerText: {
      fontWeight: "500",
      min: "0.85rem",
      max: "0.85rem",
      lineHeight: "1",
    },
    mainButtonText: {
      fontWeight: "600",
      min: "1.15rem",
      max: "1.25rem",
      lineHeight: "2rem",
    },
    step: {
      fontWeight: "600",
      min: "0.5rem",
      max: "0.875rem",
      lineHeight: "1",
    },
    fontfamily: "Helvetica",
    fontcdn: "",
  },
  colors: {
    primaryGradient: "#111111",
    secondaryGradient: "#475268",
    secondaryColor: "#252525",
    whiteColor: "#FFFFFF",
    inactived: "#979797",
    inactiveColor: "#979797",
    errorColor: "#dc251c",
    partnerHighlights: "#ffffff",
    gradientDeg: "180deg",
    primaryGradientPoint: "0%",
    secundaryGradientPoint: "100%",
    primaryMesh: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    gradient: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    lightness: "dark" as "light" | "dark",
    useSystemTheme: false,
  },
  styles: defaultThemeStyles,
};

const defaultView: IViewColorConfig = {
  background: "#fff",
  iconColors: ["#000", "#000"],
  backgroundIcon: "#fff",
  title: "#000",
  subtitile: "#000",
  bodyCopy: "#000",
  footerColor: "#000",
  backgroundBtn: "#000",
  textColorBtn: "#fff",
  stepsColors: "#000",
  dropzoneColors: ["#000", "#000"],
  stepsLabelColor: "#000",
  errorColor: "red",
  viewConfig: "errorView",
};

export const themeFormattedInitialState: IViewConfig = {
  primaryMeshGradientView: defaultView,
  specialView: defaultView,
  dataView: defaultView,
  whiteView: defaultView,
  errorView: defaultView,
};

export const APP_ROUTES = {
  LOADING_PAGE: "/",
  LANDING_PAGE: "/landing-page",
  TRANSACTIONS_PAGE: "/transactions-page",
  TRANSFER_PAGE: "/transfer-page",
  ADD_CARD_PAGE: "/add-card-page",
  ADD_CARD_ADDRESS_PAGE: "/add-card-address",
  ADD_CARD_PROCESSING_PAGE: "/add-card-processing",
  ADD_CARD_COLLECT_PAGE: "/add-card-collect-page",
  SENDING_MONEY_PAGE: "/sending-money-page",
  CIP_PAGE: "/cip",
  PHONE_NUMBER_PAGE: "/phone-number",
  ERROR_PAGE: "/error-page",
  SESSION_EXPIRED_PAGE: "/session-expired",
  SERVICE_UNAVAILABLE_PAGE: "/service-unavailable",
  RECEIPT_SUMMARY_PAGE: "/receipt-summary/:operationId",
  SUPPORT_PAGE: "/support",
  LEGAL_PAGE: "/legal",
  CANCELLATION_PAGE: "/cancellation",
};

export const cardNumberPlaceHolder = "XXXX XXXX XXXX XXXX";
export const validCards = "Visa & Mastercard";

export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "VI", name: "Virgin Islands" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;

// DEPRECATED: Use flavor system from 'src/flavors' instead
// These are kept for backwards compatibility
import { getFlavorClassName } from "../flavors";

export let isFlavorB = config.TRANSFER_FLAVOR === "B";
export let flavorClassName = getFlavorClassName();
/** @deprecated Kit AuraLayout manages white-view-background when useSystemTheme + dark. */
export const isWhiteViewByFlavor = "";

/**
 * Fill for page-level kit `ElevatedCircle`s (landing, error, sending screens).
 * The kit applies `background` inline, so we route it through a CSS variable
 * that follows the same cascade `.gradient-card` has (see index.css).
 */
export const ELEVATED_CIRCLE_BACKGROUND = "var(--elevated-circle-bg)";

/**
 * @deprecated Use flavor system from 'src/flavors' instead
 */
export function setTransferFlavor(flavor: string) {
  console.warn(
    "[DEPRECATED] setTransferFlavor is deprecated. Use flavor system from src/flavors instead."
  );
  config.TRANSFER_FLAVOR = flavor;
  isFlavorB = config.TRANSFER_FLAVOR === "B";
  flavorClassName = getFlavorClassName().toLocaleLowerCase();
}

function detectBrowserThemeMode(): "dark" | "light" {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyThemeToDOM(mode: "dark" | "light") {
  const root = document.documentElement;
  root.setAttribute("data-theme", mode);
  root.style.colorScheme = mode;
  // Keep in sync with Lola AuraLayout (html.light / html.dark)
  root.classList.toggle("light", mode === "light");
  root.classList.toggle("dark", mode === "dark");
}

export let themeMode: "dark" | "light" =
  config.THEME_MODE === "dark" || config.THEME_MODE === "light"
    ? config.THEME_MODE
    : detectBrowserThemeMode();
export let isDarkMode = themeMode === "dark";

applyThemeToDOM(themeMode);

export function setThemeMode(mode: string) {
  config.THEME_MODE = mode;
  themeMode = mode === "dark" ? "dark" : "light";
  isDarkMode = themeMode === "dark";
  applyThemeToDOM(themeMode);
}
